// assets/edm-gallery.js — macOS-Dock smooth & snappy + neighbor-guard (Dawn 15.3)

/**
 * EDM-GALLERY.JS (EXPERIENCE & INTERACTION ENGINE)
 * --------------------------------------------------
 * ROLE: Verantwortlich für das High-End Look & Feel (UX).
 * Experience Engine (UX & Animation)
 * Setzt oben auf und veredelt die User Experience
 * TASKS:
 * - Desktop: Fisheye-Effekt (macOS Dock) für die Thumbnails.
 * - Mobile: Optimierung des Snapping-Verhaltens und Swipe-Gefühls.
 * - Animation: Nutzt kritisch gedämpfte Federsysteme für weiche Bewegungen.
 * STRATEGY: Setzt 'True Scaling' und 'Locked Geometry' visuell in Szene.
 */

(() => {
    const fine = matchMedia('(pointer:fine)').matches;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fine || reduced) return;
  
    const list =
      document.querySelector('[data-edm-thumb-list]') ||
      document.querySelector('.edm-thumb-list') ||
      document.querySelector('.thumbnail-list');
    if (!list) return;
  
    const items = Array.from(
      list.querySelectorAll('[data-edm-thumb], .edm-thumb, .thumbnail')
    );
    if (!items.length) return;
  
    list.classList.add('edm-js-fisheye');
  
    // === Tuning (snappier) ===================================================
    const SCALE_HOVER    = 1.13;
    const SCALE_NEIGHBOR = 1.05;
    const LIFT_HOVER     = -3;
    const LIFT_NEIGHBOR  = -1;
  
    // Kritisch gedämpfte Federn (c ≈ 2*sqrt(k)): schneller & ohne Wobble
    const K_SCALE = 130;                     // ↑ reaktiver
    const C_SCALE = 2 * Math.sqrt(K_SCALE);  // ≈ 22.8
    const K_LIFT  = 180;
    const C_LIFT  = 2 * Math.sqrt(K_LIFT);   // ≈ 26.8
  
    // Strengere Stop-Schwellen
    const EPS_S  = 0.0012;
    const EPS_VS = 0.0012;
    const EPS_Y  = 0.08;
    const EPS_VY = 0.08;
  
    const state = items.map(() => ({ s:1, vs:0, y:0, vy:0, ts:1, ty:0 }));
  
    items.forEach(el => {
      el.style.willChange = 'transform';
      el.style.transformOrigin = 'bottom center';
      el.style.zIndex = '0';
    });
  
    let centers = [];
    function measureCenters(){
      centers = items.map(el => {
        const r = el.getBoundingClientRect();
        return r.left + r.width / 2;
      });
    }
    measureCenters();
    const ro = 'ResizeObserver' in window ? new ResizeObserver(measureCenters) : null;
    if (ro) ro.observe(list);
    addEventListener('resize', measureCenters, { passive:true });
  
    let hoverIdx = -1;
    let running = false;
    let rafId = 0;
    let lastT = 0;
    let leaveTimer = 0;
  
    function closestIndex(x){
      let bestI = -1, bestD = Infinity;
      for (let i = 0; i < centers.length; i++){
        const d = Math.abs(x - centers[i]);
        if (d < bestD){ bestD = d; bestI = i; }
      }
      return bestI;
    }
  
    // --- FIX: neighbor-guard -------------------------------------------------
    function applyTargets(){
      if (hoverIdx < 0){
        for (let i = 0; i < items.length; i++){ state[i].ts = 1; state[i].ty = 0; }
        return;
      }
      for (let i = 0; i < items.length; i++){
        if (i === hoverIdx){
          state[i].ts = SCALE_HOVER;    state[i].ty = LIFT_HOVER;
        } else if (i === hoverIdx - 1 || i === hoverIdx + 1){
          state[i].ts = SCALE_NEIGHBOR; state[i].ty = LIFT_NEIGHBOR;
        } else {
          state[i].ts = 1;              state[i].ty = 0;
        }
      }
    }
  
    function step(now){
      if (!running) return;
      const t = now || performance.now();
      const dt = Math.min((t - lastT) / 1000, 0.05); // clamp
      lastT = t;
  
      applyTargets();
  
      let alive = false;
      for (let i = 0; i < items.length; i++){
        const st = state[i];
  
        // Scale spring
        const ds = st.ts - st.s;
        const as = K_SCALE * ds - C_SCALE * st.vs;
        st.vs += as * dt;
        st.s  += st.vs * dt;
  
        // Lift spring
        const dy = st.ty - st.y;
        const ay = K_LIFT * dy - C_LIFT * st.vy;
        st.vy += ay * dt;
        st.y  += st.vy * dt;
  
        items[i].style.transform = `translateY(${st.y.toFixed(2)}px) scale(${st.s.toFixed(3)})`;
        items[i].style.zIndex = (i === hoverIdx ? 2 : (i === hoverIdx - 1 || i === hoverIdx + 1 ? 1 : 0)).toString();
  
        if (Math.abs(ds) > EPS_S || Math.abs(st.vs) > EPS_VS ||
            Math.abs(dy) > EPS_Y || Math.abs(st.vy) > EPS_VY) { alive = true; }
      }
  
      if (alive) {
        rafId = requestAnimationFrame(step);
      } else {
        running = false;
        if (hoverIdx === -1) {
          clearTimeout(leaveTimer);
          leaveTimer = setTimeout(hardReset, 60);
        }
      }
    }
  
    function kickLoop(){
      if (!running){
        running = true;
        lastT = performance.now();
        rafId = requestAnimationFrame(step);
      }
    }
  
    function hardReset(){
      for (let i = 0; i < items.length; i++){
        const st = state[i];
        st.s = st.ts = 1; st.vs = 0;
        st.y = st.ty = 0; st.vy = 0;
        items[i].style.transform = 'translateY(0px) scale(1)';
        items[i].style.zIndex = '0';
      }
    }
  
    list.addEventListener('pointerenter', (e) => {
      clearTimeout(leaveTimer);
      measureCenters();
      hoverIdx = closestIndex(e.clientX);
      kickLoop();
    }, { passive:true });
  
    list.addEventListener('pointermove', (e) => {
      const evts = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : null;
      const x = evts && evts.length ? evts[evts.length - 1].clientX : e.clientX;
      hoverIdx = closestIndex(x);
      kickLoop();
    }, { passive:true });
  
    list.addEventListener('pointerleave', () => {
      hoverIdx = -1;            // → applyTargets() setzt ALLE auf Baseline (Bug behoben)
      kickLoop();
      clearTimeout(leaveTimer);
      leaveTimer = setTimeout(hardReset, 100); // kurzer finaler Snap
    }, { passive:true });
  
    addEventListener('visibilitychange', () => {
      if (document.hidden && rafId) cancelAnimationFrame(rafId);
    });
  })();
  
  
  // edumon — Judge.me: Anzahl aus Badge extrahieren und neben die Sterne setzen
  (() => {
    const root = document.querySelector('.edm-mobile-header .jdgm-prev-badge');
    if (!root) return;
  
    const stars = root.querySelector('.jdgm-prev-badge__stars');
    if (!stars) return;
  
    // 1) Count bevorzugt aus data-Attribut (Judge.me liefert das oft mit)
    let count =
      root.getAttribute('data-number-of-reviews') ||
      root.getAttribute('data-reviews-count') || '';
  
    // 2) Fallback: aus dem (verborgenen) Text parsen
    if (!count) {
      const raw = (root.querySelector('.jdgm-prev-badge__text')?.textContent || '').trim();
      const m = raw.match(/(\d+)\s*(?:review|bewert)/i);
      if (m) count = m[1];
    }
  
    // 3) Einfügen/aktualisieren
    if (count) {
      let badge = root.querySelector('.edm-rating-count');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'edm-rating-count';
        stars.after(badge);
      }
      badge.textContent = `(${count})`;
    }
  })();
  
  
/**
 * EDUMON Mobile Gallery Navigation
 * ---------------------------------
 * Based on Multicolumn Blueprint Pattern
 * Uses data attributes instead of hardcoded section IDs
 */
(() => {
  const initEdmGalleryMobile = () => {
    // Find all gallery roots with our data attribute
    document.querySelectorAll('[data-edm-gallery-root]').forEach(root => {
      // Skip if already initialized
      if (root.dataset.edmGalleryInit === 'true') return;
      root.dataset.edmGalleryInit = 'true';

      const list = root.querySelector('.product__media-list');
      const nav = root.querySelector('[data-edm-gallery-nav]');
      
      if (!list) return;
      
      const slides = Array.from(list.querySelectorAll('.slider__slide, .product__media-item'));
      if (slides.length <= 1) return;

      // Get navigation elements (if nav exists)
      const dots = nav ? Array.from(nav.querySelectorAll('.js-gallery-dot')) : [];
      const prevBtn = nav ? nav.querySelector('.js-gallery-prev') : null;
      const nextBtn = nav ? nav.querySelector('.js-gallery-next') : null;

      /**
       * Calculate current slide index based on scroll position
       * Uses center-point calculation like Multicolumn
       */
      const getActiveIndex = () => {
        const slide = slides[0];
        if (!slide) return 0;
        
        const gap = parseInt(window.getComputedStyle(list).gap) || 0;
        const slideWidth = slide.offsetWidth + gap;
        
        // Center-based calculation
        const scrollCenter = list.scrollLeft + list.clientWidth / 2;
        let bestIdx = 0;
        let bestDist = Infinity;
        
        slides.forEach((s, i) => {
          const slideCenter = s.offsetLeft + s.offsetWidth / 2;
          const dist = Math.abs(scrollCenter - slideCenter);
          if (dist < bestDist) {
            bestDist = dist;
            bestIdx = i;
          }
        });
        
        return bestIdx;
      };

      /**
       * Update UI state (dots + arrows)
       * Mirrors Multicolumn's update() function
       */
      const update = () => {
        const index = getActiveIndex();
        
        // Update dots
        dots.forEach((d, i) => {
          d.classList.toggle('is-active', i === index);
          d.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        
        // Update arrow disabled states (like Multicolumn)
        if (prevBtn) {
          prevBtn.disabled = list.scrollLeft <= 10;
        }
        if (nextBtn) {
          nextBtn.disabled = list.scrollLeft >= (list.scrollWidth - list.clientWidth - 10);
        }

        // Sync Dawn's is-active class on slides
        slides.forEach((s, i) => {
          s.classList.toggle('is-active', i === index);
        });
      };

      /**
       * Scroll to specific slide index
       * Mirrors Multicolumn's go() function
       */
      const goToSlide = (targetIndex) => {
        const slide = slides[targetIndex];
        if (!slide) return;
        
        // Calculate scroll position for center alignment
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const containerCenter = list.clientWidth / 2;
        const scrollTarget = slideCenter - containerCenter;
        
        list.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      };

      // === Event Listeners ===
      
      // Scroll sync with requestAnimationFrame
      let rafId = 0;
      list.addEventListener('scroll', () => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(update);
      }, { passive: true });

      // Dot clicks
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => goToSlide(i));
      });

      // Arrow clicks
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          const currentIdx = getActiveIndex();
          goToSlide(Math.max(0, currentIdx - 1));
        });
      }
      
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          const currentIdx = getActiveIndex();
          goToSlide(Math.min(slides.length - 1, currentIdx + 1));
        });
      }

      // MutationObserver for Dawn's class changes (variant switching)
      const mo = new MutationObserver(() => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(update);
      });
      mo.observe(list, { subtree: true, attributes: true, attributeFilter: ['class'] });

      // Initial state
      setTimeout(update, 200);
    });
  };

  // Initialize on DOM ready and Shopify section events
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEdmGalleryMobile);
  } else {
    initEdmGalleryMobile();
  }
  document.addEventListener('shopify:section:load', initEdmGalleryMobile);
})();
  